import { Component, ElementRef, HostListener, Input, ViewChild, inject } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';

import { HeadlessService } from '@novu/headless';

import { Constants } from '../../../core/const/constants';
import { HelperFunctionService } from '../../../core/services/helper-function.service';
import { Router } from '@angular/router';
import { BroadcastChannelService } from '../../../core/services/cross-tab-authentication.service';
import { OrgAppInfoHelperService } from '../../../core/services/org-app-info-helper.service';
import { firstValueFrom } from 'rxjs';
import { AppIconUrlTransformPipe } from '../../../core/pipes/appicon-url-transform.pipe';
import { TabSdk } from '@techextensor/tab-sdk';
import { PermissionService } from '../../../core/services/permission.service';
import { environment } from '../../../../environments/environment';
import { StorageConstants } from '@techextensor/tab-core-utility';

@Component({
    selector: 'app-switcher',
    imports: [CommonModule, AppIconUrlTransformPipe, NgIf],
    templateUrl: './tab-app-switch.component.html',
    styleUrl: './tab-app-switch.component.scss'
})
export class AppSwtichComponent {
  public headlessService: HeadlessService;
  private readonly router: Router = inject(Router);
  private readonly _permissionService: PermissionService = inject(PermissionService);
  private readonly _helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
  private readonly _broadcastChannelService: BroadcastChannelService = inject(BroadcastChannelService);
  private readonly _orgAppInfoHelper: OrgAppInfoHelperService = inject(OrgAppInfoHelperService);

  @ViewChild('popup') popup: ElementRef | undefined;
  selectedTenant: any;
  selectedAppId: any;
  isPopupVisible?: boolean;
  isLoading?: boolean;
  appList: any;
  isAppListVisible?: boolean;
  tenantList: any;
  canShowCreateAppOption = false;
  @Input() isAdminRoute: boolean = false;

  /**
   * Creates a new instance of the HeadlessService.
   * This method is called only once, during the component's initialization.
   * It sets the application identifier, subscriber ID, backend URL, and socket URL
   * for the HeadlessService.
   * @returns {HeadlessService} The instance of the HeadlessService.
   */
  constructor() {
    let personId = this.getPersonIdFromLocalStorage();
    this.headlessService = new HeadlessService({
      subscriberId: personId,
      applicationIdentifier: environment.commonConfig.applicationIdentifier,
      backendUrl: environment.commonConfig.backendUrl,
      socketUrl: environment.commonConfig.socketUrl
    });
  }
  /**
   * Retrieves the person ID of the currently logged in user from local storage.
   * If the user is not logged in, it returns null.
   * @returns {string | null} The person ID of the currently logged in user, or null if the user is not logged in.
   */
  getPersonIdFromLocalStorage() {
    let loginUserData = TabSdk.store.getUserInfo()?.user;
    return loginUserData?.Attributes?.PersonId?.toLowerCase() || null;
  }

  ngOnInit(): void {
    this.selectedTenant = TabSdk.store.tenantId;
    this.selectedAppId = TabSdk.store.appId;
    this.getTenantListfromLoggedInUser();
    this.canShowCreateAppOption = (
      this._permissionService.doesUserIsTabAdministrator() || this._permissionService.doesUserIsTenantAdministrator()
    ) && !this.isAdminRoute;
  }

  getTenantListfromLoggedInUser() {
    // Show loader before making the API call
    this.isLoading = true;

    // Execute the API call
    this._helperFunctionService
      .getDSQData(Constants.Get_Tenants_TABD_Tenant_Users, { Id: TabSdk.store.userId })
      .subscribe((res: any) => {
        // Check if API call is successful
        if (res && res?.Result?.length > 0) {
          // Update organization list
          this.tenantList = res.Result;
          // Fetch the list of apps for the selected tenant
          this.getAppListByTenant(this.selectedTenant);
          // Hide loader after API call completes
          this.isLoading = false;
        }
      });
  }

  // Tenant shortener
  getInitials(phrase: string): string {
    return phrase
      .split(' ') // Split the phrase into words
      .map(word => word[0]) // Get the first letter of each word
      .join(''); // Join them into a single string
  }
  /**
   * Sets the selected organization and makes an API call to fetch tenant data.
   *
   * @param tenant - The selected tenant object
   */
  getAppListByTenant(tenant: any) {
    // Show loader before making the API call
    this.isLoading = true;

    // Execute the API call to fetch tenant data
    this._helperFunctionService
      .getDSQData(Constants.Get_AppList_TenantAppUserSubscriptionsView, { TenantId: tenant })
      .subscribe((res: any) => {
        // Check if API call is successful
        if (res && res?.Result?.length > 0) {
          // Update app list
          this.appList = res.Result;
          this.showAppSwitcher();
          // Hide loader after API call completes
          this.isLoading = false;
        }
      });
  }
  async selectedApp(appDetail: any, tenatId: string) {
    const currentTenant = TabSdk.store.tenantId;
    const authData = this._broadcastChannelService.getAuthData();
    const transferData = { authData, appCode: appDetail.AppName };

    if (currentTenant?.toLowerCase() === appDetail?.TenantId?.Id?.toLowerCase()) {
      this._orgAppInfoHelper.setAppInfo({
        ...appDetail,
        Id: appDetail.AppId,
      });

      this.router.navigate(['initializing'], {
        fragment: this._helperFunctionService.getAuthTransferFragment(transferData),
        replaceUrl: true
      });

    } else {
      const { subdomain, domain } = this._orgAppInfoHelper.extractDomainInfo();

      if (this._orgAppInfoHelper.isValidDomain(domain)) {
        const newSubdomain = appDetail?.TenantId?.Subdomain;
        window.location.replace(this._helperFunctionService.buildAuthTransferUrl(`https://${newSubdomain}.${domain}/initializing`, transferData));

      } else {
        const orgInfoRes: any = await firstValueFrom(this._orgAppInfoHelper.getOrgInfoApiObservable({ Id: appDetail.TenantId.Id }));
        const data = orgInfoRes?.Result?.[0];
        if (data && typeof data?.LocaleSettings === 'string') {
          data.LocaleSettings = JSON.parse(data.LocaleSettings)
        }

        TabSdk.store.setOrgInfo(data);

        this._orgAppInfoHelper.setAppInfo({
          ...appDetail,
          Id: appDetail.AppId,
        });

        window.location.replace(this._helperFunctionService.buildAuthTransferUrl('initializing', transferData));
      }
    }

  }

  showAppSwitcher() {
    this.isAppListVisible = this.tenantList?.length > 1 || (this.tenantList?.length === 1 && this.appList?.length > 1);
  }
  toggleContentVisibility() {
    // Toggle the visibility of the popup content
    this.isPopupVisible = !this.isPopupVisible;
  }
  @HostListener('document:mousedown', ['$event'])
  public onOutsideClick(event: any): void {
    if (this.isPopupVisible && this.popup && !this.popup.nativeElement.contains(event.target)) {
      this.isPopupVisible = false;
    }
  }
  addApp() {
    const appInfo = TabSdk.store.getAppInfo() ?? {};
    const appEnv = TabSdk.store.environmentId;
    TabSdk.store.setSession(StorageConstants.appNavigationMapping, {appInfo, appEnv});
    
    const appDetail = {
      AppName: environment.commonConfig.applicationCode,
      DisplayName: environment.commonConfig.applicationCode,
      Id: environment.commonConfig.applicationId,
      TenantId: { Id: environment.commonConfig.tenantId }
    };
    this._orgAppInfoHelper.setAppInfo(appDetail);
    TabSdk.store.environmentId = environment.id;

    this.router.navigate([`admin/${Constants.MarketPlace_ScreenID}`]);
  }
}